import { useEffect, useMemo } from 'react';
import { Minus, Plus, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import {
  WIDGET_CATEGORIES,
  WIDGET_REGISTRY,
  isWidgetAllowed,
  type CanFn,
  type WidgetDef,
} from './widgets/widgetRegistry';

interface WidgetCatalogProps {
  /** Ids already present on the dashboard — shown with a remove button. */
  usedIds: Set<string>;
  can: CanFn;
  onAdd: (def: WidgetDef) => void;
  onRemove: (id: string) => void;
  onClose: () => void;
}

/**
 * The widget catalog: a modal listing every widget the caller is allowed to
 * see, grouped by category, usable at any moment. Add appends the widget,
 * remove takes it off the dashboard; the modal stays open so widgets can be
 * toggled in one go. In view mode the page persists each change immediately;
 * in edit mode the changes land on the draft.
 */
export function WidgetCatalog({ usedIds, can, onAdd, onRemove, onClose }: WidgetCatalogProps) {
  const { t } = useI18n();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const groups = useMemo(
    () =>
      WIDGET_CATEGORIES.map((category) => ({
        category,
        widgets: WIDGET_REGISTRY.filter(
          (def) => def.category === category && isWidgetAllowed(def, can),
        ),
      })).filter((g) => g.widgets.length > 0),
    [can],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-border bg-card shadow-card-hover">
        <div className="sticky top-0 flex items-center justify-between border-b border-border bg-card px-5 py-3">
          <h2 className="font-heading text-lg font-semibold text-foreground">
            {t('dashboard.catalog.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground transition hover:bg-gray-200 hover:text-foreground"
            aria-label={t('common.close')}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="space-y-6 p-5">
          {groups.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('dashboard.catalog.empty')}</p>
          )}
          {groups.map(({ category, widgets }) => (
            <section key={category}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t(`dashboard.category.${category}`)}
              </h3>
              <ul className="mt-2 space-y-1">
                {widgets.map((def) => {
                  const Icon = def.icon;
                  const added = usedIds.has(def.id);
                  return (
                    <li
                      key={def.id}
                      className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-200 text-muted-foreground">
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">
                          {t(def.nameKey)}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">{t(def.descKey)}</p>
                      </div>
                      {added ? (
                        <button
                          type="button"
                          onClick={() => onRemove(def.id)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground transition hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-400"
                        >
                          <Minus className="h-3.5 w-3.5" aria-hidden />
                          {t('dashboard.catalog.remove')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onAdd(def)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground transition hover:bg-gray-200"
                        >
                          <Plus className="h-3.5 w-3.5" aria-hidden />
                          {t('dashboard.catalog.add')}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
